import { z } from 'zod';
import { futuresProductTermsSchema, resolveFuturesLastTrade, resolveFuturesScheduledMinute, futuresContractLastTradeSchema } from './futuresReference.js';
import { auditFuturesContractCoverage } from './futuresDataCoverage.js';
const id = z.number().int().positive().max(4_294_967_295);
const uint = z.number().int().nonnegative().max(4_294_967_295);
const clock = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const unsigned = z
    .string()
    .regex(/^[0-9]{1,20}$/)
    .refine((v) => v === BigInt(v).toString() && BigInt(v) < 18446744073709551615n);
const price = z
    .string()
    .regex(/^-?[0-9]{1,19}$/)
    .refine((v) => v === BigInt(v).toString() && BigInt(v) >= -9223372036854775808n && BigInt(v) < 9223372036854775807n);
export const futuresSelectionDefinitionSchema = z
    .object({
    id,
    artifactId: id,
    productId: id,
    contractId: id,
    rawSymbol: z.string().min(1).max(80),
    publisherId: z.number().int().nonnegative().max(65535),
    instrumentId: uint,
    eventNs: unsigned,
    receiveNs: unsigned,
    activationNs: unsigned.nullable(),
    expirationNs: unsigned.nullable(),
    minPriceIncrementRaw: price.nullable(),
    updateAction: z.enum(['A', 'M', 'D'])
})
    .strict();
export const futuresDefinitionImportIntervalSchema = z
    .object({ artifactId: id, rawSymbol: z.string().min(1).max(80), startNs: unsigned, endNs: unsigned })
    .strict()
    .refine((v) => BigInt(v.endNs) > BigInt(v.startNs));
const candidateSchema = z
    .object({
    contractId: id,
    canonicalSymbol: z.string().min(1).max(80),
    definitions: z.array(futuresSelectionDefinitionSchema).max(20000),
    definitionImports: z.array(futuresDefinitionImportIntervalSchema).max(20000),
    lastTrades: z.array(futuresContractLastTradeSchema).max(32)
})
    .strict();
const second = 1000000000n;
export const futuresContractSelectionSchema = z
    .object({
    productId: id,
    contractId: id,
    canonicalSymbol: z.string().min(1).max(80),
    atTimestamp: clock,
    minimumSecondsBeforeLastTrade: uint,
    definitionId: id,
    definitionArtifactId: id,
    definitionKnownAtNs: unsigned,
    definitionImportArtifactIds: z.array(id).min(1).max(20000),
    rawSymbol: z.string().min(1).max(80),
    publisherId: z.number().int().nonnegative().max(65535),
    instrumentId: uint,
    lastTradeId: id,
    lastTradingTimestamp: clock
})
    .strict();
/** Provider snapshot boundary is UTC weekday midnight, including Friday carry across weekends. */
export function futuresDefinitionSnapshotAnchorNs(startNs) {
    const time = BigInt(unsigned.parse(startNs));
    const day = 86400n * second;
    let anchor = (time / day) * day;
    const weekday = new Date(Number(anchor / 1000000n)).getUTCDay();
    if (weekday === 0)
        anchor -= 2n * day;
    if (weekday === 6)
        anchor -= day;
    return (anchor < 0n ? 0n : anchor).toString();
}
/** Calendar-based elapsed-time roll within an explicit universe. No price/status/volume fallback selection. */
export function selectFuturesContract(input) {
    id.parse(input.productId);
    clock.parse(input.atTimestamp);
    uint.parse(input.minimumSecondsBeforeLastTrade);
    const candidates = z.array(candidateSchema).min(1).max(31).parse(input.candidates);
    if (new Set(candidates.map((v) => v.contractId)).size !== candidates.length)
        throw new Error('Repeated futures selection candidate');
    if (candidates.reduce((n, v) => n + v.definitions.length + v.definitionImports.length, 0) > 20000)
        throw new Error('Futures selection context exceeds its aggregate bound');
    const at = BigInt(input.atTimestamp) * second;
    if (at >= 18446744073709551615n)
        throw new Error('Futures selection clock overflow');
    const anchor = BigInt(futuresDefinitionSnapshotAnchorNs(at.toString()));
    const eligible = [];
    const unavailable = (reason, contractId) => ({ state: 'unavailable', reason, contractId });
    for (const candidate of candidates.sort((a, b) => a.contractId - b.contractId)) {
        if (candidate.definitions.some((v) => v.productId !== input.productId || v.contractId !== candidate.contractId) ||
            candidate.lastTrades.some((v) => v.productId !== input.productId || v.contractId !== candidate.contractId))
            throw new Error('Futures selection evidence belongs to another instrument');
        const cutoff = resolveFuturesLastTrade(candidate.lastTrades, input.productId, candidate.contractId, input.atTimestamp);
        if (cutoff.state === 'tradingEnded')
            continue;
        const imports = candidate.definitionImports.filter((v) => BigInt(v.startNs) <= at && BigInt(v.endNs) > anchor);
        const symbols = [...new Set(imports.map((v) => v.rawSymbol))];
        if (!symbols.length)
            return unavailable('definitionImportMissing', candidate.contractId);
        const usedImports = new Set();
        for (const symbol of symbols) {
            let through = anchor;
            for (const range of imports
                .filter((v) => v.rawSymbol === symbol)
                .sort((a, b) => (BigInt(a.startNs) < BigInt(b.startNs) ? -1 : BigInt(a.startNs) > BigInt(b.startNs) ? 1 : 0))) {
                if (BigInt(range.startNs) > through)
                    break;
                if (BigInt(range.endNs) > through) {
                    through = BigInt(range.endNs);
                    usedImports.add(range.artifactId);
                }
            }
            if (through <= at)
                return unavailable('definitionImportMissing', candidate.contractId);
        }
        const records = candidate.definitions
            .map((v) => ({ ...v, knownAt: BigInt(v.eventNs) > BigInt(v.receiveNs) ? BigInt(v.eventNs) : BigInt(v.receiveNs) }))
            .filter((v) => v.knownAt >= anchor && v.knownAt <= at)
            .sort((a, b) => (a.knownAt < b.knownAt ? -1 : a.knownAt > b.knownAt ? 1 : 0));
        if (!records.length)
            continue; // Complete snapshot/update imports establish absence within this explicit universe.
        if (records.some((v) => !symbols.includes(v.rawSymbol)))
            return unavailable('definitionImportMissing', candidate.contractId);
        const first = records[0];
        if (!first)
            throw new Error('Missing validated futures definition');
        if (first.knownAt !== anchor && first.updateAction !== 'A')
            return unavailable('definitionUnknown', candidate.contractId);
        // A late delivery of an older exchange event cannot undo a newer lifecycle state.
        // Distinct receive clocks order same-exchange-clock updates; exact clock ties remain ambiguous.
        const newestEvent = records.reduce((maximum, v) => (BigInt(v.eventNs) > maximum ? BigInt(v.eventNs) : maximum), 0n);
        const newestReceive = records
            .filter((v) => BigInt(v.eventNs) === newestEvent)
            .reduce((maximum, v) => (BigInt(v.receiveNs) > maximum ? BigInt(v.receiveNs) : maximum), 0n);
        const simultaneous = records.filter((v) => BigInt(v.eventNs) === newestEvent && BigInt(v.receiveNs) === newestReceive);
        const latest = simultaneous[0];
        if (!latest)
            throw new Error('Missing latest futures definition');
        if (simultaneous.some((v) => v.rawSymbol !== latest.rawSymbol ||
            v.publisherId !== latest.publisherId ||
            v.instrumentId !== latest.instrumentId ||
            v.activationNs !== latest.activationNs ||
            v.expirationNs !== latest.expirationNs ||
            v.minPriceIncrementRaw !== latest.minPriceIncrementRaw ||
            v.updateAction !== latest.updateAction))
            return unavailable('definitionConflict', candidate.contractId);
        // Exact duplicates can belong to several retained files. Preserve a deterministic observation identity.
        simultaneous.sort((a, b) => a.id - b.id);
        const definition = simultaneous[0];
        if (!definition)
            throw new Error('Missing latest definition');
        if (definition.updateAction === 'D')
            continue;
        if (definition.activationNs === null)
            return unavailable('activationUnknown', candidate.contractId);
        if (BigInt(definition.activationNs) > at)
            continue;
        if (cutoff.state === 'unknown')
            return unavailable('deadlineUnknown', candidate.contractId);
        if (definition.expirationNs === null || BigInt(definition.expirationNs) !== BigInt(cutoff.lastTradingTimestamp) * second)
            return unavailable('deadlineConflict', candidate.contractId);
        if (cutoff.lastTradingTimestamp - input.atTimestamp <= input.minimumSecondsBeforeLastTrade)
            continue;
        eligible.push({
            productId: input.productId,
            contractId: candidate.contractId,
            canonicalSymbol: candidate.canonicalSymbol,
            atTimestamp: input.atTimestamp,
            minimumSecondsBeforeLastTrade: input.minimumSecondsBeforeLastTrade,
            definitionId: definition.id,
            definitionArtifactId: definition.artifactId,
            definitionKnownAtNs: definition.knownAt.toString(),
            definitionImportArtifactIds: [...usedImports].sort((a, b) => a - b),
            rawSymbol: definition.rawSymbol,
            publisherId: definition.publisherId,
            instrumentId: definition.instrumentId,
            lastTradeId: cutoff.lastTradeId,
            lastTradingTimestamp: cutoff.lastTradingTimestamp
        });
    }
    eligible.sort((a, b) => a.lastTradingTimestamp - b.lastTradingTimestamp);
    const selected = eligible[0];
    if (!selected)
        return { state: 'unavailable', reason: 'noEligibleContract', contractId: null };
    if (eligible[1]?.lastTradingTimestamp === selected.lastTradingTimestamp)
        return { state: 'unavailable', reason: 'ambiguousExpiry', contractId: null };
    return { state: 'selected', selection: selected };
}
/** Completed receive-minute open research proxy only; never an exact matching-engine fill or live quote. */
export function resolveFuturesMinutePriceEvidence(input) {
    const selection = futuresContractSelectionSchema.parse(input.selection);
    if (input.bars.length > 1 || input.statuses.length > 20000 || input.imports.length > 20000)
        throw new Error('Futures minute evidence exceeds its bound');
    clock.parse(input.observedThroughTimestamp);
    const start = selection.atTimestamp, end = start + 60;
    if (start % 60 || input.observedThroughTimestamp < end)
        throw new Error('A futures minute price requires completed evidence at its exact boundary');
    const terms = futuresProductTermsSchema.parse(input.terms);
    const definition = futuresSelectionDefinitionSchema.parse(input.definition);
    if (terms.productId !== selection.productId ||
        terms.availableTimestamp > start ||
        terms.effectiveFromTimestamp > start ||
        (terms.effectiveUntilTimestamp !== null && terms.effectiveUntilTimestamp < end) ||
        definition.id !== selection.definitionId ||
        definition.productId !== selection.productId ||
        definition.contractId !== selection.contractId ||
        definition.artifactId !== selection.definitionArtifactId ||
        definition.rawSymbol !== selection.rawSymbol ||
        definition.publisherId !== selection.publisherId ||
        definition.instrumentId !== selection.instrumentId ||
        (BigInt(definition.eventNs) > BigInt(definition.receiveNs) ? definition.eventNs : definition.receiveNs) !==
            selection.definitionKnownAtNs ||
        BigInt(selection.definitionKnownAtNs) > BigInt(start) * second ||
        definition.updateAction === 'D' ||
        definition.activationNs === null ||
        BigInt(definition.activationNs) > BigInt(start) * second ||
        definition.expirationNs !== (BigInt(selection.lastTradingTimestamp) * second).toString() ||
        selection.lastTradingTimestamp - start <= selection.minimumSecondsBeforeLastTrade)
        throw new Error('Futures minute evidence does not match its frozen selection/terms');
    if (definition.minPriceIncrementRaw !== terms.tickSizeRaw)
        return { state: 'unavailable', reason: 'definitionTickMismatch' };
    const session = resolveFuturesScheduledMinute(input.sessions, selection.productId, start);
    if (session.state === 'unknown')
        return { state: 'unavailable', reason: 'calendarUnknown' };
    if (session.state === 'scheduledClosed')
        return { state: 'unavailable', reason: 'scheduledClosed' };
    const cutoff = resolveFuturesLastTrade(input.lastTrades, selection.productId, selection.contractId, start);
    if (cutoff.state !== 'beforeLastTrade' ||
        cutoff.lastTradeId !== selection.lastTradeId ||
        cutoff.lastTradingTimestamp !== selection.lastTradingTimestamp)
        return { state: 'unavailable', reason: 'deadlineUnavailable' };
    if (cutoff.lastTradingTimestamp < end)
        return { state: 'unavailable', reason: 'crossesDeadline' };
    const endingCutoff = resolveFuturesLastTrade(input.lastTrades, selection.productId, selection.contractId, end - 1);
    if (endingCutoff.state !== 'beforeLastTrade' ||
        endingCutoff.lastTradeId !== cutoff.lastTradeId ||
        input.sessions.some((v) => v.productId === selection.productId &&
            v.availableTimestamp > start &&
            v.availableTimestamp < end &&
            v.startTimestamp < end &&
            v.endTimestamp > start))
        return { state: 'unavailable', reason: 'referenceChanged' };
    const window = { productId: selection.productId, contractId: selection.contractId, startTimestamp: start, endTimestamp: end };
    const report = auditFuturesContractCoverage(window, input.bars, input.statuses, input.imports, terms.tickSizeRaw);
    if (report.counts.invalidBarMinutes)
        throw new Error('Invalid futures price evidence');
    if (!report.acquisitionComplete)
        return { state: 'unavailable', reason: 'sourceImportMissing' };
    if (!input.bars.length)
        return { state: 'unavailable', reason: 'priceMissing' };
    // Run the same causal status pass with an empty bar set to distinguish stable state and transitions.
    const status = auditFuturesContractCoverage(window, [], input.statuses, input.imports, terms.tickSizeRaw).counts;
    if (status.absentDuringStatusTransition)
        return { state: 'unavailable', reason: 'statusTransition' };
    if (status.absentWithNonTradingStatus)
        return { state: 'unavailable', reason: 'notTrading' };
    if (status.absentWithUnknownStatus)
        return { state: 'unavailable', reason: 'statusUnknown' };
    const bar = input.bars[0];
    if (!bar)
        throw new Error('Missing validated futures bar');
    id.parse(bar.artifactId);
    return {
        state: 'usableForMinuteModel',
        model: 'completedReceiveMinuteOpen',
        priceRaw: bar.openRaw,
        bucketStartTimestamp: start,
        evidenceAvailableTimestamp: end,
        barArtifactId: bar.artifactId,
        termsId: terms.id,
        sessionDayId: session.sessionDayId,
        lastTradeId: cutoff.lastTradeId,
        officialTradeDate: session.tradeDate
    };
}
//# sourceMappingURL=futuresSelection.js.map